/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  APP.TSX - NOTOMORROW3D MAIN APPLICATION                                  ║
 * ║  "Humanity's Last Stand Against THALIA" - 3D Holographic Resistance HQ    ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 * 
 * Layout (holo redesign, round 4 "Ops Console"):
 * - Left nav rail (Play, War Room, Leaderboard, History, How it works, Thesis) + 12-col grid
 * - Big centered countdown gauge over the Three.js THALIA core
 * - Final-5-minute urgency ramp (yellow -> orange -> red), see src/holo/urgency.ts
 * - Mobile (<600px): Spinner-style Play tab with a bottom tab bar
 */

import { useState, useEffect, useCallback, useRef, lazy, Suspense, type ReactNode } from 'react';
import { Toaster } from 'react-hot-toast';
import { motion } from 'framer-motion';

// ═══════════════════════════════════════════════════════════════════════════
// CRITICAL PATH - Always loaded immediately
// Timer, Pot, Buy Keys, Dividends = 80% of user experience
// ═══════════════════════════════════════════════════════════════════════════
import BuyKeys from './components/BuyKeys';
import YourKeysPanel from './components/YourKeysPanel';
import KeeperReferral from './components/KeeperReferral';
import NetworkBanner from './components/NetworkBanner';
import StickyTimer from './components/StickyTimer';
import EventMoments from './components/EventMoments';
import DangerZone, { useDangerZoneClass } from './components/DangerZone';
import WinnerCelebration from './components/WinnerCelebration';
import { useFactionTheme } from './hooks/useFactionTheme';
import type { ChaosEvent } from './components/ChaosChiron';
import { isComingChaosEvent } from './config/onchainFeatures';

// ═══════════════════════════════════════════════════════════════════════════
// LAZY LOADED - Non-critical, loaded on demand
// Reduces initial bundle size for faster mobile load
// ═══════════════════════════════════════════════════════════════════════════
const WalletConnect = lazy(() => import('./components/WalletConnect'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Leaderboard = lazy(() => import('./components/Leaderboard'));
const SeasonsLive = lazy(() => import('./components/SeasonsLive'));
const StakesPanel = lazy(() => import('./components/StakesPanel'));
const WarRoom = lazy(() => import('./components/FactionWarRoom'));
const ChaosRoundPanel = lazy(() => import('./components/ChaosRoundPanel'));
import { SEASONS_LIVE, CHAOS_PANEL_ENABLED } from './config/onchainFeatures';
const Referral = lazy(() => import('./components/Referral'));
const RoundHistory = lazy(() => import('./components/RoundHistory'));
const RoundReplay = lazy(() => import('./components/RoundReplay'));
import { DronesFaq, HumansVsDrones } from './components/Drones';
import LoreDrops from './components/LoreDrops';
const Sharing = lazy(() => import('./components/Sharing'));
const HowToPlay = lazy(() => import('./components/HowToPlay'));
const ThesisPage = lazy(() => import('./components/ThesisPage'));
import './components/thesis.css';
const LiveActivityFeed = lazy(() => import('./components/LiveActivityFeed'));
const TransactionHistory = lazy(() => import('./components/TransactionHistory'));
const LiveTicker = lazy(() => import('./components/LiveTicker'));
const DegenTitles = lazy(() => import('./components/DegenTitles'));
const PushNotifications = lazy(() => import('./components/PushNotifications'));

// Named export lazy wrappers
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
// PriceChart removed pre-launch: $FRAME has not launched and has no price.
const ThaliaBar = lazy(() => import('./holo/ThaliaBar'));
const ProfilePage = lazy(() => import('./holo/ProfilePage'));
// /admin: owner-only console, not linked from navigation (own chunk).
const AdminPage = lazy(() => import('./admin/AdminPage'));
import { SiteAnnouncement, FactionWarsChip } from './components/SiteContent';
const ChaosChiron = lazy(() => import('./components/ChaosChiron').then(m => ({ default: m.ChaosChiron })));
const StreakDisplay = lazy(() => import('./components/StreakDisplay').then(m => ({ default: m.StreakDisplay })));
const NetworkStatusBar = lazy(() => import('./components/NetworkStatusBar').then(m => ({ default: m.NetworkStatusBar })));
const TermsOfService = lazy(() => import('./components/TermsOfService').then(m => ({ default: m.TermsOfService })));

// UX Improvement Components - Information hierarchy & contextual education
const ActiveConditions = lazy(() => import('./components/ActiveConditions').then(m => ({ default: m.ActiveConditions })));
const GameLog = lazy(() => import('./components/GameLog').then(m => ({ default: m.GameLog })));
const ResistancePoints = lazy(() => import('./components/ResistancePoints').then(m => ({ default: m.ResistancePoints })));
const THALIAHint = lazy(() => import('./components/THALIAHint').then(m => ({ default: m.THALIAHint })));

// Error handling
import { ErrorBoundary, SafeSection } from './components/ErrorBoundary';

// Always loaded - core hooks & providers
import { NotificationProvider } from './components/NotificationSystem';
import { SoundProvider, useSoundEffects } from './hooks/useSoundEffects';
import { useVisualEffects } from './hooks/useVisualEffects';
import { useGameEvents, type GameEvent } from './hooks/useGameEvents';
import { useEventExplainer, EventExplainerOverlay } from './components/EventExplainer';
import { HelpButton } from './components/Glossary';
import { DemoModeProvider, useDemoMode, DemoBanner, DemoStartModal } from './components/DemoMode';
import { DemoSiteBanner } from './components/DemoSiteBanner';
import FaucetButton from './components/FaucetButton';
import { BetaWarningBanner } from './components/BetaWarningBanner';
import { PlayerNameModal } from './components/PlayerNameModal';
import { NarrativeToast } from './components/NarrativeToast';
import type { EventType } from './services/narrative';
import { InstallPrompt } from './components/InstallPrompt';
import { TeamChat } from './components/TeamChat';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { OfflineBanner } from './components/OfflineBanner';
import HowItWorksOverlay, { useHowItWorks } from './components/HowItWorksOverlay';
import FactionLeaderboard from './components/FactionLeaderboard';

// Holo console (round 4 design)
import './holo/holo.css';
import HoloCore from './holo/HoloCore';
import HoloGauge from './holo/HoloGauge';
import UrgencyOverlay, { SoundToggle, useCountdownSound } from './holo/UrgencyOverlay';
import { HoloBackground, ThaliaFeed, Waveform, AnimatedNumber, type FeedLine } from './holo/HoloFx';
import AgeGate from './holo/AgeGate';
import PreviewControls from './holo/PreviewControls';
import { useUrgency, useFinalPreview } from './holo/urgency';

// Utils
import { getGameContract } from './utils/contract';
import { TIMER_INFO_ABI, fetchTimerInfo, toRoundTimer, type RoundTimer } from './utils/roundTimer';
import { fetchPAWPrice } from './utils/price';
import { toast } from 'react-hot-toast';
import { ethers } from 'ethers';

// Helper to format FRAME amounts for narrative display
function formatFRAMEForNarrative(pawWei: string): string {
  try {
    const amount = parseFloat(ethers.formatEther(pawWei));
    if (amount >= 1_000_000_000_000_000) return `${(amount / 1_000_000_000_000_000).toFixed(1)}Q FRAME`;
    if (amount >= 1_000_000_000_000) return `${(amount / 1_000_000_000_000).toFixed(1)}T FRAME`;
    if (amount >= 1_000_000_000) return `${(amount / 1_000_000_000).toFixed(1)}B FRAME`;
    if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M FRAME`;
    if (amount >= 1_000) return `${(amount / 1_000).toFixed(1)}K FRAME`;
    return `${amount.toFixed(0)} FRAME`;
  } catch {
    return 'some FRAME';
  }
}

type Tab = 'play' | 'war' | 'leaderboard' | 'history' | 'howto' | 'thesis' | 'terms' | 'profile' | 'admin';

const RAIL_TABS: Array<{ id: Tab; label: string; short: string; icon: string }> = [
  { id: 'play', label: 'Play', short: 'Play', icon: '◎' },
  { id: 'war', label: 'War Room', short: 'War Room', icon: '⬡' },
  { id: 'leaderboard', label: 'Leaderboard', short: 'Ranks', icon: '≡' },
  { id: 'history', label: 'History', short: 'History', icon: '◷' },
  { id: 'howto', label: 'How it works', short: 'Guide', icon: '?' },
];

const shortAddr = (a?: string) => (a && /^0x0+$/.test(a) ? '—' : a && a.length > 10 ? `${a.slice(0, 5)}…${a.slice(-4)}` : a || '—');
const compactNum = (n: number) => n.toLocaleString('en-US', { notation: 'compact', maximumFractionDigits: 2 });
const toFrame = (v: bigint) => { try { return parseFloat(ethers.formatEther(v)); } catch { return 0; } };

const INITIAL_FEED: FeedLine[] = [
  { id: 'i1', ts: Date.now() - 60000, text: 'Testnet demo online. Every key adds', hi: '+30s' },
  { id: 'i2', ts: Date.now() - 120000, text: 'Buys use test FRAME only · test tokens have no value' },
  { id: 'i3', ts: Date.now() - 180000, text: 'Factions report to the War Room · pick your cell' },
  { id: 'i4', ts: Date.now() - 240000, text: '$FRAME has not launched · nobody holds it yet' },
];

/** Holo panel shell (corner brackets, header row). Module-level so children never remount. */
function Panel({ title, meta, span, className = '', children, tilt = true }: { title: string; meta?: ReactNode; span: string; className?: string; children: ReactNode; tilt?: boolean }) {
  return (
    <div className={`holo-panel ${tilt ? 'tilt' : ''} ${span} ${className}`}>
      <div className="ph"><span className="lbl">{title}</span>{meta !== undefined && <span className="mono">{meta}</span>}</div>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN APP COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function App() {
  return (
    <ErrorBoundary name="App">
      <SoundProvider>
        <DemoModeProvider>
          <AppContent />
        </DemoModeProvider>
      </SoundProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  // ─────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────
  const [account, setAccount] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(-1); // -1 = loading, 0 = round ended
  const [roundDeadline, setRoundDeadline] = useState<number>(0); // Unix timestamp of the EFFECTIVE round deadline (utils/roundTimer)
  const [roundTimer, setRoundTimer] = useState<RoundTimer | null>(null);
  const [pot, setPot] = useState<bigint>(0n);
  const [jackpotPool, setJackpotPool] = useState<bigint>(0n);
  const [currentPrice, setCurrentPrice] = useState<bigint>(0n);
  const [lastBuyer, setLastBuyer] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [referrerFromUrl, setReferrerFromUrl] = useState<string | null>(null);
  // Age gate: the console is the landing view; the gate is a modal over it.
  const [showLanding, setShowLanding] = useState<boolean>(() => {
    // Dev bypass: ?dev or ?preview=final in URL skips the gate for testing
    const q = window.location.search;
    if (q.includes('dev') || q.includes('preview=final')) return false;
    try { return localStorage.getItem('nt3d_disclaimer_accepted') !== 'true'; } catch { return true; }
  });
  // URL routing: /thesis (#thesis, /lore), /profile, /profile/:address; everything else is the SPA root.
  const parseRoute = (): { page: Tab; addr: string | null } => {
    try {
      if (/^#(thesis|lore)$/.test(window.location.hash) || /\/(thesis|lore)\/?$/.test(window.location.pathname)) return { page: 'thesis', addr: null };
      if (/^\/admin\/?$/.test(window.location.pathname)) return { page: 'admin', addr: null };
      const hm = window.location.pathname.match(/^\/history(?:\/(\d+))?\/?$/);
      if (hm) return { page: 'history', addr: hm[1] ?? null };
      const m = window.location.pathname.match(/^\/profile(?:\/(0x[0-9a-fA-F]{40}))?\/?$/);
      if (m) return { page: 'profile', addr: m[1] ? m[1].toLowerCase() : null };
    } catch { /* ignore */ }
    return { page: 'play', addr: null };
  };
  const [currentPage, setCurrentPageRaw] = useState<Tab>(() => parseRoute().page);
  const [profileAddr, setProfileAddr] = useState<string | null>(() => parseRoute().addr);
  const setCurrentPage = useCallback((p: Tab, addr: string | null = null) => {
    setCurrentPageRaw(p);
    if (p === 'profile' || p === 'history') setProfileAddr(addr);
    window.scrollTo(0, 0);
    try {
      const want = p === 'thesis' ? '/thesis' : p === 'admin' ? '/admin' : p === 'profile' ? (addr ? `/profile/${addr}` : '/profile') : p === 'history' ? (addr ? `/history/${addr}` : '/history') : '/';
      const cur = /^#(thesis|lore)$/.test(window.location.hash) ? '/thesis' : window.location.pathname.replace(/\/$/, '') || '/';
      if (cur !== want) history.pushState(null, '', want + window.location.search);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    const onPop = () => { const r = parseRoute(); setCurrentPageRaw(r.page); setProfileAddr(r.addr); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useEffect(() => {
    try { if (/^#lore$/.test(window.location.hash) || /\/lore\/?$/.test(window.location.pathname)) history.replaceState(null, '', '/thesis' + window.location.search); } catch { /* ignore */ }
  }, []);
  const [buyPulse, setBuyPulse] = useState(0);
  const [feed, setFeed] = useState<FeedLine[]>(INITIAL_FEED);
  const pushFeed = useCallback((text: string, hi?: string) => {
    setFeed(prev => [{ id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ts: Date.now(), text, hi }, ...prev].slice(0, 12));
  }, []);
  const [isEndingRound, setIsEndingRound] = useState(false);
  const [currentRoundId, setCurrentRoundId] = useState<number>(1);
  // Blood Moon: every 10th round. Dev preview: ?bloodmoon=1 (force on) / ?bloodmoon=0 (force off)
  const bmParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('bloodmoon') : null;
  const isBloodMoon = bmParam === '1' ? true : bmParam === '0' ? false : (currentRoundId > 0 && currentRoundId % 10 === 0);
  useEffect(() => {
    document.body.classList.toggle('blood-moon', isBloodMoon);
    return () => document.body.classList.remove('blood-moon');
  }, [isBloodMoon]);
  const [showPlayerNameModal, setShowPlayerNameModal] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [totalKeysSold, setTotalKeysSold] = useState<number>(0);
  const [recentPurchaseCount, setRecentPurchaseCount] = useState<number>(0);
  const [chaosRecentlyTriggered, setChaosRecentlyTriggered] = useState(false);
  
  // New feature states
  const [showTeamChat, setShowTeamChat] = useState(false);
  const [playerTeam, setPlayerTeam] = useState<number>(0);
  const howItWorks = useHowItWorks();
  
  // Sticky timer & winner celebration states
  const [showStickyTimer, setShowStickyTimer] = useState(false);
  const [showWinnerCelebration, setShowWinnerCelebration] = useState(false);
  const [roundWinner, setRoundWinner] = useState<string>('');
  const [winnerPot, setWinnerPot] = useState<bigint>(0n);
  const [winnerRoundId, setWinnerRoundId] = useState<number>(0);
  const heroRef = useRef<HTMLDivElement>(null);
  const buyKeysRef = useRef<HTMLDivElement>(null);
  
  // Chaos visual state
  const [extinctionActive, setExtinctionActive] = useState(false);
  const [flashCrashActive, setFlashCrashActive] = useState(false);
  const [fomoSurgeActive, setFomoSurgeActive] = useState(false);
  const [assassinActive, setAssassinActive] = useState(false);
  const [carnivalActive, setCarnivalActive] = useState(false);
  const [activeChaosEvents, setActiveChaosEvents] = useState<ChaosEvent[]>([]);
  
  // Narrative toast state for AI-generated event stories
  const [narrativeEvent, setNarrativeEvent] = useState<{
    type: EventType;
    team?: number;
    player?: string;
    amount?: string;
    details?: string;
  } | undefined>(undefined);
  
  // Secret sauce hooks
  useSoundEffects(); // Initialize sound system
  useVisualEffects(); // Initialize visual effects
  const demoMode = useDemoMode();
  const eventExplainer = useEventExplainer();
  
  // Faction personalization & danger zone atmosphere
  useFactionTheme(playerTeam);
  useDangerZoneClass(timeRemaining, isBloodMoon);
  
  // Intersection Observer for sticky timer
  // Re-run when currentPage/isLoading/showLanding change since heroRef only exists on the game page
  useEffect(() => {
    const el = heroRef.current;
    if (!el) { setShowStickyTimer(false); return; }
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyTimer(!entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [currentPage, isLoading]);
  
  // WebSocket for real-time game events
  const handleGameEvent = useCallback((event: GameEvent) => {
    console.log('[NT3D] WebSocket event:', event.type, event.data);
    // Gate chaos UI to mechanics that actually exist on-chain (see config/onchainFeatures.ts)
    if (isComingChaosEvent(event.type)) return;
    
    // Track recent purchase activity for THALIA context
    if (event.type === 'keys_purchased') {
      setRecentPurchaseCount(prev => prev + 1);
      setBuyPulse(p => p + 1);
      const n = parseInt(event.data.keysBought || '1') || 1;
      pushFeed(`${shortAddr(event.data.buyer)} bought`, `${n} key${n > 1 ? 's' : ''} · timer +${30 * n}s`);
    }
    
    // Track chaos events for THALIA context + visual shifts
    if (['ghost_key', 'extinction_event', 'blood_moon', 'surge_protocol', 'assassin_mode'].includes(event.type)) {
      setChaosRecentlyTriggered(true);
      setTimeout(() => setChaosRecentlyTriggered(false), 30000); // Clear after 30s
    }
    
    // Chaos visual shift triggers
    if (event.type === 'extinction_event') {
      setExtinctionActive(true);
      setTimeout(() => setExtinctionActive(false), 5000);
    }
    if (event.type === 'flash_crash') {
      setFlashCrashActive(true);
      // Flash crash has a duration, clear after event data says so or 60s default
      const duration = event.data.duration ? parseInt(event.data.duration) * 1000 : 60000;
      setTimeout(() => setFlashCrashActive(false), duration);
    }
    if (event.type === 'surge_protocol' || event.type === 'fomo_surge') {
      setFomoSurgeActive(true);
      const duration = event.data.duration ? parseInt(event.data.duration) * 1000 : 60000;
      setTimeout(() => setFomoSurgeActive(false), duration);
    }
    if (event.type === 'assassin_mode') {
      setAssassinActive(true);
      const duration = event.data.duration ? parseInt(event.data.duration) * 1000 : 120000;
      setTimeout(() => setAssassinActive(false), duration);
    }
    if (event.type === 'chaos_carnival') {
      setCarnivalActive(true);
      const duration = event.data.duration ? parseInt(event.data.duration) * 1000 : 60000;
      setTimeout(() => setCarnivalActive(false), duration);
    }
    
    // Feed chaos events to ChaosChiron ticker
    const chaosTypes = [
      'flash_crash', 'fomo_surge', 'surge_protocol', 'extinction_event',
      'ghost_key', 'blood_moon', 'assassin_mode', 'chaos_carnival',
      'volcanic_jackpot', 'charity_chaos', 'groundhog_day', 'whale_alert',
    ];
    if (chaosTypes.includes(event.type)) {
      const chaosEvent: ChaosEvent = {
        id: `${event.type}-${Date.now()}`,
        type: event.type as ChaosEvent['type'],
        message: event.data.message || `${event.type.replace(/_/g, ' ').toUpperCase()} triggered!`,
        timestamp: Date.now(),
        duration: event.data.duration ? parseInt(event.data.duration) * 1000 : 30000,
        severity: ['extinction_event', 'flash_crash', 'blood_moon', 'assassin_mode'].includes(event.type) 
          ? 'critical' 
          : ['fomo_surge', 'surge_protocol', 'chaos_carnival'].includes(event.type) 
            ? 'warning' 
            : 'info',
      };
      setActiveChaosEvents(prev => [...prev.slice(-9), chaosEvent]); // Keep last 10
      pushFeed(chaosEvent.message);
      // Auto-expire after duration
      setTimeout(() => {
        setActiveChaosEvents(prev => prev.filter(e => e.id !== chaosEvent.id));
      }, chaosEvent.duration || 30000);
    }
    
    // Winner celebration trigger
    if (event.type === 'round_ended') {
      setRoundWinner(event.data.winner || '');
      setWinnerPot(BigInt(event.data.potSize || '0'));
      setWinnerRoundId(parseInt(event.data.roundId || '0'));
      setShowWinnerCelebration(true);
    }
    
    // Map WebSocket events to narrative event types (THALIA narrative)
    const eventTypeMap: Record<string, EventType> = {
      'keys_purchased': 'key_purchase',
      'round_started': 'round_start', 
      'round_ended': 'round_end',
      'assassin_mode': 'assassin_mode',       // TERMINAL
      'blood_moon': 'blood_moon_start',       // SURGE
      'surge_protocol': 'blood_moon_start',   // THALIA naming
      'ghost_key': 'ghost_key',               // PHANTOM
      'extinction_event': 'timer_critical',   // RECALIBRATION
    };
    
    const narrativeType = eventTypeMap[event.type];
    if (!narrativeType) return;
    
    // Only show narratives for significant events (not every single key purchase)
    const isSignificant = 
      event.type !== 'keys_purchased' || 
      (event.data.keysBought && parseInt(event.data.keysBought) >= 10) ||
      event.data.isGhostKey;
    
    if (isSignificant) {
      setNarrativeEvent({
        type: narrativeType,
        team: event.data.teamId,
        player: event.data.buyer || event.data.winner,
        amount: event.data.pawAmount ? formatFRAMEForNarrative(event.data.pawAmount) : undefined,
        details: event.data.message,
      });
    }
  }, [pushFeed]);
  
  const { connected: wsConnected } = useGameEvents({
    autoConnect: true,
    onEvent: handleGameEvent,
  });
  
  // Decay recent purchase counter every 60s to simulate a rolling window
  useEffect(() => {
    const decay = setInterval(() => {
      setRecentPurchaseCount(prev => Math.max(0, prev - 1));
    }, 60000);
    return () => clearInterval(decay);
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // DEMO MODE: Computed display values (switches between real and demo data)
  // ─────────────────────────────────────────────────────────────────────────
  const displayTimeRemaining = demoMode.isDemo ? demoMode.demo.timeRemaining : timeRemaining;
  const displayPot = demoMode.isDemo ? demoMode.demo.pot : pot;
  const displayJackpotPool = demoMode.isDemo ? demoMode.demo.pot / 10n : jackpotPool; // Demo jackpot is 10% of pot
  const displayCurrentPrice = demoMode.isDemo ? demoMode.demo.keyPrice : currentPrice;
  const displayLastBuyer = demoMode.isDemo ? (demoMode.demo.lastBuyer || 'Demo Player') : lastBuyer;
  // Phase 1.1 empty-round fast clock (only when the contract reports it; never in demo mode)
  const fastClock = !demoMode.isDemo && !!roundTimer?.fastClock && displayTimeRemaining > 0;
  const emptyRound = !demoMode.isDemo && !!roundTimer?.emptyRound;
  // Note: displayRoundId available via demoMode.isDemo ? demoMode.demo.round : currentRoundId

  // ─────────────────────────────────────────────────────────────────────────
  // FINAL COUNTDOWN URGENCY (+ ?preview=final / Ctrl+Shift+F local preview)
  // ─────────────────────────────────────────────────────────────────────────
  const finalPreview = useFinalPreview();
  const clockTime = finalPreview.enabled ? finalPreview.t : displayTimeRemaining;
  const { urgency, urgencyRef } = useUrgency(clockTime);
  const { soundOn, toggleSound, beat } = useCountdownSound();
  const prevClock = useRef(clockTime);
  useEffect(() => {
    // any upward jump of the clock (a +30s key buy, real or demo) pulses the core
    if (clockTime > prevClock.current + 1 && prevClock.current > 0) setBuyPulse(p => p + 1);
    prevClock.current = clockTime;
  }, [clockTime]);
  const { simulateBuy } = finalPreview;
  const simulatePreviewBuy = useCallback(() => {
    simulateBuy();
    pushFeed('PREVIEW · simulated key bought', 'clock +30s');
  }, [simulateBuy, pushFeed]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // PARSE URL REFERRER
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    const KEY = 'nt3d_referrer';
    if (ref && /^0x[0-9a-fA-F]{40}$/.test(ref)) {
      setReferrerFromUrl(ref);
      try { localStorage.setItem(KEY, ref); } catch { /* storage unavailable */ }
      return;
    }
    try {
      const stored = localStorage.getItem(KEY);
      if (stored && /^0x[0-9a-fA-F]{40}$/.test(stored)) setReferrerFromUrl(stored);
    } catch { /* storage unavailable */ }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // FETCH PLAYER TEAM (for team chat)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchPlayerTeam = async () => {
      if (!account) {
        setPlayerTeam(0);
        return;
      }
      try {
        const contract = await getGameContract();
        if (!contract) return;
        // getPlayerStats() is not routed by the live diamond; getPlayerInfo() (View facet) carries teamId.
        const info = await new ethers.Contract(contract.target, ['function getPlayerInfo(address) view returns (uint256 keysOwned, uint8 teamId, uint256 lastPurchaseTime, uint256 playerReferralEarnings, uint256 lifetimeKeys, uint256 lifetimeSpent, uint256 roundsWon, uint256 sniperKills)'], contract.runner).getPlayerInfo(account);
        setPlayerTeam(Number(info.teamId || 0));
      } catch (error) {
        console.error('Failed to fetch player team:', error);
      }
    };
    fetchPlayerTeam();
  }, [account, refreshTrigger]);

  // FETCH FRAME PRICE (for display only, not for pricing)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchPAWPriceForDisplay = async () => {
      try {
        await fetchPAWPrice();
        // FRAME price is now handled by individual components
      } catch (error) {
        console.error('Failed to fetch FRAME price:', error);
      }
    };
    
    fetchPAWPriceForDisplay();
    
    // Update price every 10 seconds for more accurate pricing
    const interval = setInterval(fetchPAWPriceForDisplay, 10000);
    return () => clearInterval(interval);
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // FETCH GAME DATA
  // ─────────────────────────────────────────────────────────────────────────
  const fetchGameData = useCallback(async () => {
    try {
      console.log('[NT3D] Fetching game data...');
      const gameContract = await getGameContract();
      if (!gameContract) {
        console.warn('[NT3D] Game contract not available');
        setIsLoading(false);
        return;
      }
      
      console.log('[NT3D] Contract ready, calling getRoundInfo...');
      // Fetch current round data (contract uses getRoundInfo not getCurrentRoundInfo)
      // Note: We use getFullKeyPrice() instead of roundData.currentPrice because
      // getCurrentKeyPrice() in the contract uses all-time totalKeys instead of
      // round-specific totalKeysSold, causing price to not reset between rounds.
      // Phase 1.1: getTimerInfo() (when routed) is the contract's effective deadline, the same one
      // endRoundIfExpired() and the keeper use; otherwise fall back to getRoundInfo().
      const timerContract = new ethers.Contract(gameContract.target, TIMER_INFO_ABI, gameContract.runner);
      const [roundData, jackpot, correctPrice, timerInfo] = await Promise.all([
        gameContract.getRoundInfo(),
        gameContract.jackpotPool(),
        gameContract.getFullKeyPrice(), // This uses round-specific key count
        fetchTimerInfo(() => timerContract.getTimerInfo()),
      ]);
      const timer = toRoundTimer(timerInfo, roundData);
      
      console.log('[NT3D] Round data received:', {
        roundId: Number(roundData.roundId),
        pot: roundData.pot?.toString(),
        timeRemaining: Number(roundData.timeRemaining)
      });
      
      const deadline = timer.deadline;
      const newTimeRemaining = timer.timeRemaining;
      setRoundTimer(timer);
      setRoundDeadline(deadline);
      setTimeRemaining(newTimeRemaining);
      setPot(roundData.pot || 0n);
      setJackpotPool(jackpot || 0n);
      setCurrentPrice(correctPrice || roundData.currentPrice || 0n); // Use getFullKeyPrice() for correct round-based price
      setLastBuyer(roundData.lastBuyer || '');
      setCurrentRoundId(Number(roundData.roundId || 1));
      setTotalKeysSold(timer.keysSold);
      
      // Reset ending round state if we have time remaining (fixes desync issues)
      if (newTimeRemaining > 0) {
        setIsEndingRound(false);
      }
      
    } catch (error) {
      console.error('[NT3D] Failed to fetch game data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Initial fetch
  useEffect(() => {
    fetchGameData();
  }, [fetchGameData]);
  
  // Poll for updates
  useEffect(() => {
    const interval = setInterval(fetchGameData, 10000); // Every 10 seconds
    return () => clearInterval(interval);
  }, [fetchGameData]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // COUNTDOWN TIMER
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      if (roundDeadline > 0) {
        // Calculate time from deadline for accuracy — picks up extensions automatically
        const now = Math.floor(Date.now() / 1000);
        const remaining = roundDeadline - now;
        setTimeRemaining(remaining > 0 ? remaining : 0);
      } else {
        setTimeRemaining((prev) => {
          // Don't decrement during loading (-1) or if already at 0
          if (prev <= 0) return prev;
          return prev - 1;
        });
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [roundDeadline]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // AUTO-REFRESH WHEN ROUND ENDS (keeper handles actual round ending)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Only trigger when timer reaches 0 (not -1 loading state) and not already ending
    if (timeRemaining !== 0 || isEndingRound || isLoading) return;
    
    // Show notification that round is ending
    setIsEndingRound(true);
    toast('⏰ Round complete! Finalizing prizes...', {
      duration: 10000,
      icon: '🏆',
      style: { background: '#1a1a2e', color: '#eab308', border: '1px solid #eab308' }
    });
    
    // Poll more frequently to detect new round
    const pollForNewRound = setInterval(async () => {
      try {
        const gameContract = await getGameContract();
        if (!gameContract) return;
        
        const roundData = await gameContract.getRoundInfo();
        const newTimeRemaining = Number(roundData.timeRemaining || 0);
        
        // If timer is > 0, new round has started
        if (newTimeRemaining > 0) {
          console.log('✅ New round detected! Time remaining:', newTimeRemaining);
          toast.success('🎮 New round started! Game on!', { duration: 5000 });
          setIsEndingRound(false);
          fetchGameData();
          clearInterval(pollForNewRound);
        }
      } catch (error) {
        console.error('Poll error:', error);
      }
    }, 3000); // Check every 3 seconds
    
    // Clean up after 2 minutes max
    const timeout = setTimeout(() => {
      clearInterval(pollForNewRound);
      setIsEndingRound(false);
      fetchGameData();
    }, 120000);
    
    return () => {
      clearInterval(pollForNewRound);
      clearTimeout(timeout);
    };
  }, [timeRemaining, isEndingRound, fetchGameData]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────────────────
  const fetchGameDataRef = useRef<() => void>(() => {});
  fetchGameDataRef.current = () => { void fetchGameData(); };
  const handleWalletConnect = useCallback((address: string | null) => {
    setAccount(address);
    fetchGameDataRef.current();
  }, []);
  
  const handlePurchase = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
    setBuyPulse(p => p + 1);
    pushFeed('Your key buy registered ·', 'clock +30s per key');
    if (demoMode.isDemo) return;
    fetchGameData();
    // Delayed re-fetch to catch timer extension after block confirmation
    setTimeout(() => fetchGameData(), 3000);
    setTimeout(() => fetchGameData(), 6000);
  }, [fetchGameData, demoMode.isDemo, pushFeed]);
  
  // ─────────────────────────────────────────────────────────────────────────
  // ADMIN ACCESS - Redirect to separate admin site
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'A') {
        event.preventDefault();
        // Redirect to admin site (protected by Cloudflare Access)
        window.open('https://admin.nt3d.fun', '_blank');
        console.log('🔓 Redirecting to admin site');
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // SHOW LANDING PAGE IF USER HASN'T ACCEPTED DISCLAIMER
  // ─────────────────────────────────────────────────────────────────────────
  // (The age gate now renders as a modal over the console — see <AgeGate /> below.)
  
  // ─────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ─────────────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div
          className="relative z-10 text-center glass-panel-3d p-12"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          {/* Animated loading indicator */}
          <div className="relative w-24 h-24 mx-auto mb-8">
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-neon-cyan/30"
              animate={{ rotate: 360 }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="absolute inset-2 rounded-full border-2 border-neon-magenta/30"
              animate={{ rotate: -360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="absolute inset-4 rounded-full border-2 border-neon-purple/30"
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-3xl">◈</span>
            </motion.div>
          </div>
          
          <h1 className="font-orbitron text-2xl text-transparent bg-clip-text bg-gradient-to-r from-neon-cyan via-neon-magenta to-neon-purple mb-3">
            INITIALIZING NEURAL LINK
          </h1>
          <p className="text-gray-500 text-sm font-mono">Connecting to blockchain...</p>
        </motion.div>
      </div>
    );
  }
  
  // ─────────────────────────────────────────────────────────────────────────
  // MAIN RENDER — Holo Ops Console
  // ─────────────────────────────────────────────────────────────────────────
  const isDemoSite = import.meta.env.VITE_DEMO_MODE === 'true';
  const potNum = toFrame(displayPot);
  const priceNum = toFrame(displayCurrentPrice);
  const scrollToBuy = () => {
    if (currentPage !== 'play') setCurrentPage('play');
    setTimeout(() => buyKeysRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };
  const loader = <div className="flex items-center justify-center min-h-[120px]"><div className="w-8 h-8 border-2 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" /></div>;
  return (
    <NotificationProvider>
    <div className="holo-root">
      <HoloBackground />
      {/* Offline Detection Banner — shows when internet drops */}
      <OfflineBanner />
      <HowItWorksOverlay
        open={howItWorks.open && currentPage === 'play' && !showLanding && !finalPreview.enabled}
        onClose={howItWorks.dismiss}
        timeRemaining={displayTimeRemaining}
        pot={displayPot}
        account={account}
        isDemoSite={isDemoSite}
        onBuyFirstKey={() => {
          scrollToBuy();
          window.dispatchEvent(new Event('nt3d:prefill-first-key'));
        }}
      />

      {/* Age gate over the console (console is the landing view) */}
      {showLanding && currentPage !== 'thesis' && (
        <AgeGate
          onAccept={() => setShowLanding(false)}
          onReadLore={() => setCurrentPage('thesis')}
        />
      )}

      {/* Final-5-minute urgency: vignette, final-minute banner, big seconds, optional heartbeat */}
      <UrgencyOverlay timeRemaining={clockTime} urgency={urgency} soundOn={soundOn} beat={beat} />
      {finalPreview.enabled && (
        <PreviewControls t={finalPreview.t} onBuy={simulatePreviewBuy} onJump={finalPreview.jump} onExit={finalPreview.exit} />
      )}

      <DangerZone
        timeRemaining={displayTimeRemaining}
        isBloodMoon={isBloodMoon}
        chaosActive={chaosRecentlyTriggered}
        extinctionActive={extinctionActive}
        flashCrashActive={flashCrashActive}
        fomoSurgeActive={fomoSurgeActive}
        assassinActive={assassinActive}
        carnivalActive={carnivalActive}
      />

      <SafeSection name="EventMoments"><EventMoments account={account} faction={playerTeam} /></SafeSection>

      {/* Sticky Timer - Appears when hero section scrolls out of view */}
      <StickyTimer
        timeRemaining={clockTime}
        pot={displayPot}
        currentPrice={displayCurrentPrice}
        lastBuyer={displayLastBuyer}
        visible={showStickyTimer && currentPage === 'play'}
        onBuyClick={scrollToBuy}
      />

      {showWinnerCelebration && (
        <WinnerCelebration
          winner={roundWinner}
          potSize={winnerPot}
          roundId={winnerRoundId}
          isBloodMoon={winnerRoundId % 10 === 0}
          onDismiss={() => setShowWinnerCelebration(false)}
        />
      )}

      <DemoSiteBanner walletAddress={account || undefined} />
      <BetaWarningBanner />
      <DemoBanner />
      <DemoStartModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} onStart={() => setShowDemoModal(false)} />
      {account && (
        <PlayerNameModal isOpen={showPlayerNameModal} onClose={() => setShowPlayerNameModal(false)} address={account} />
      )}
      <EventExplainerOverlay explanation={eventExplainer.activeExplanation} onDismiss={eventExplainer.dismiss} />

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'rgba(7, 10, 38, 0.92)',
            color: '#eef0ff',
            border: '1px solid rgba(70, 222, 248, 0.3)',
            borderRadius: '4px',
            backdropFilter: 'blur(10px)',
            fontFamily: "'Exo 2', sans-serif",
            boxShadow: '0 0 20px rgba(70, 222, 248, 0.12)',
          },
        }}
      />

      <div className="holo-app">
        {/* ═══ LEFT NAV RAIL ═══ */}
        <aside className="holo-rail" aria-label="Console navigation">
          {RAIL_TABS.map(t => (
            <button key={t.id} type="button" className={`tab ${currentPage === t.id ? 'on' : ''}`} onClick={() => setCurrentPage(t.id)} aria-current={currentPage === t.id ? 'page' : undefined}>
              <i>{t.icon}</i>{t.label}
            </button>
          ))}
          <button type="button" className={`tab ${currentPage === 'thesis' ? 'on' : ''}`} onClick={() => setCurrentPage('thesis')} aria-current={currentPage === 'thesis' ? 'page' : undefined}><i>◈</i>Thesis</button>
          <div style={{ marginTop: 'auto' }} className="holo-panel">
            <div className="lbl" style={{ fontSize: 12 }}>Network</div>
            <div className="mono" style={{ fontSize: 13, color: 'var(--t2)' }}>Testnet<br />test tokens only<br />{wsConnected ? <span style={{ color: 'var(--g)' }}>● live feed</span> : <span style={{ color: 'var(--y)' }}>● polling</span>}</div>
            {!demoMode.isDemo && (
              <button type="button" className="holo-btn ghost" style={{ width: '100%', marginTop: 10, height: 38 }} onClick={() => setShowDemoModal(true)}>Practice demo</button>
            )}
          </div>
          <div className="holo-frame" style={{ marginTop: 10 }}>◇ $FRAME · not launched</div>
        </aside>

        <div className="holo-main">
          {/* ═══ TOP BAR ═══ */}
          <header className="holo-top">
            <div className="holo-brand">
              <img src="/logo.jpg" alt="" />
              <div><div className="bn">NO TOMORROW <span>3D</span></div><div className="bs">nt3d.fun</div></div>
            </div>
            <div className="tr">
              <span className="holo-demo top-badge" aria-label="Testnet demo">● TESTNET<span className="tb-x"> DEMO</span></span>
              <button type="button" className={`holo-btn ghost sm hideM hdr-help ${currentPage === 'howto' ? 'on' : ''}`} onClick={() => setCurrentPage('howto')} aria-label="How it works" title="How it works">?</button>
              <HelpButton inline />
              <button type="button" className={`holo-btn ghost sm hideM th-hdr ${currentPage === 'profile' ? 'on' : ''}`} onClick={() => setCurrentPage('profile')}>Profile</button>
              <button type="button" className={`holo-btn ghost sm th-hdr ${currentPage === 'thesis' ? 'on' : ''}`} onClick={() => setCurrentPage('thesis')}>Thesis</button>
              <SoundToggle on={soundOn} onToggle={toggleSound} />
              <div className="hideM"><PushNotifications timeRemaining={timeRemaining} compact={true} /></div>
              <div className="hideM"><LanguageSwitcher variant="compact" /></div>
              <FaucetButton account={account} ownsEvent />
              <Suspense fallback={null}>
                <WalletConnect onConnect={handleWalletConnect} account={account} onEditName={() => setShowPlayerNameModal(true)} onOpenProfile={() => setCurrentPage('profile')} />
              </Suspense>
            </div>
          </header>

          <NetworkBanner />
      <KeeperReferral />
          <SiteAnnouncement />
          {/* ═══ STATUS STRIP ═══ */}
          <div className="holo-status">
            <span>ROUND <b>#{currentRoundId}</b></span>
            <span>KEY <b>{priceNum ? (priceNum >= 1e6 ? compactNum(priceNum) : priceNum.toLocaleString('en-US', { maximumFractionDigits: 4 })) : '—'} test FRAME</b></span>
            <span>KEYS <b>{totalKeysSold.toLocaleString('en-US')}</b></span>
            <span>BUYS/MIN <b className="live">{recentPurchaseCount}</b></span>
            <span style={{ marginLeft: 'auto' }}>SYS {wsConnected ? <b className="live">● LIVE</b> : <b className="sync">● SYNCING</b>}</span>
          </div>
          {/* ═══ EVENT SLOT: dedicated row for live event chips (Blood Moon, Chaos, Danger…) ═══ */}
          <div className="holo-events">
            <div id="holo-event-slot" className="holo-event-chips" aria-live="polite" />
            <FactionWarsChip />
            <SafeSection name="LiveTicker"><Suspense fallback={null}>
              <LiveTicker roundId={currentRoundId} timeRemaining={displayTimeRemaining} pot={potNum} lastBuyer={displayLastBuyer} buysPerMin={recentPurchaseCount} isBloodMoon={isBloodMoon} fastClock={fastClock} />
            </Suspense></SafeSection>
          </div>

          <Suspense fallback={loader}>
          {currentPage === 'play' && (
            <div className="holo-grid">
              {/* THALIA bar is position:fixed (bottom HUD); it takes no grid space */}
              <SafeSection name="THALIA"><Suspense fallback={null}>
                <ThaliaBar roundId={currentRoundId} timeRemaining={displayTimeRemaining} pot={potNum} lastBuyer={displayLastBuyer} buysPerMin={recentPurchaseCount} isBloodMoon={isBloodMoon} account={account} fastClock={fastClock} />
              </Suspense></SafeSection>
              {activeChaosEvents.length > 0 && (
                <div className="s12">
                  <SafeSection name="ChaosChiron"><Suspense fallback={null}><ChaosChiron events={activeChaosEvents} position="top" /></Suspense></SafeSection>
                </div>
              )}

              {/* Humans vs Drones: renders only when the on-chain drone rules are on */}
              <HumansVsDrones roundId={currentRoundId} refresh={refreshTrigger} />

              {/* YOUR KEYS: holdings, share and every claim in one place */}
              <Panel title="Your keys" meta="test FRAME · testnet" span="s12">
                <div data-tutorial="dividends">
                  <SafeSection name="YourKeys"><YourKeysPanel currentRoundId={currentRoundId} refreshTrigger={refreshTrigger} onClaimed={() => setRefreshTrigger(prev => prev + 1)} onGoToBuy={scrollToBuy} /></SafeSection>
                </div>
              </Panel>

              {/* ROUND CLOCK HERO */}
              <div className="holo-panel s7 holo-clock-panel" data-tutorial="timer" ref={heroRef}>
                <div className="ph"><span className="lbl">Round clock</span>{fastClock
                  ? <span className="mono fast-clock" role="note" title={`No keys sold yet: an empty round ends ${Math.round((roundTimer?.emptyRoundDuration || 15540) / 60)} min after it starts. The first key keeps the time left on this clock and adds +30s per key; after that, normal rules.`}>empty round — fast clock</span>
                  : <span className="mono">24h cap · +30s / key</span>}</div>
                <div className="holo-hero">
                  <div className="holo-core">
                    <HoloCore urgencyRef={urgencyRef} pulseKey={buyPulse} />
                    <HoloGauge timeRemaining={clockTime} roundId={currentRoundId} urgency={urgency} />
                  </div>
                  <div className="hs">
                    <div className="holo-stat"><div className="k">Pot (test)</div><div className="v pot">{potNum >= 1e6 ? compactNum(potNum) : <AnimatedNumber value={potNum} />} <small style={{ fontSize: 15 }}>test FRAME</small></div></div>
                    <div className="holo-stat"><div className="k">Last buyer</div><div className="v" style={{ fontSize: 20 }}>{shortAddr(displayLastBuyer)}</div></div>
                    <div className="holo-stat"><div className="k">Jackpot pool (test)</div><div className="v" style={{ fontSize: 22 }}>{toFrame(displayJackpotPool) >= 1e6 ? compactNum(toFrame(displayJackpotPool)) : <AnimatedNumber value={toFrame(displayJackpotPool)} />} <small style={{ fontSize: 13 }}>test FRAME</small></div></div>
                  </div>
                </div>
              </div>

              {/* BUY KEYS */}
              <div className="s5 holo-panel holo-buy" ref={buyKeysRef} data-tutorial="buy-keys">
                <div className="ph"><span className="lbl">Buy keys</span><span className="mono">test FRAME / key</span></div>
                <BuyKeys
                  account={account}
                  currentPrice={displayCurrentPrice}
                  onPurchase={handlePurchase}
                  referrerFromUrl={referrerFromUrl}
                  fomoSurgeActive={fomoSurgeActive}
                  flashCrashActive={flashCrashActive}
                  isAssassinMode={assassinActive}
                  timeRemaining={displayTimeRemaining}
                  lastBuyer={displayLastBuyer}
                  emptyRound={emptyRound}
                />
                <div className="mono" style={{ fontSize: 12, color: '#9fc3c9', marginTop: 8, textAlign: 'center' }}>Testnet · test FRAME only · no real value · buys lock in the final 15s</div>
              </div>

              <SafeSection name="ActiveConditions"><Suspense fallback={null}>
                <div className="s12">
                  <ActiveConditions timeLeft={displayTimeRemaining} isBloodMoon={isBloodMoon} isAssassinMode={assassinActive} isFlashCrash={flashCrashActive} isFOMOSurge={fomoSurgeActive} cascadeLevel={0} />
                </div>
              </Suspense></SafeSection>

              <Panel title="War Room · faction standings" meta="share of keys" span="s7">
                <SafeSection name="FactionRings"><FactionLeaderboard variant="rings" playerFaction={playerTeam} refreshTrigger={refreshTrigger} /></SafeSection>
              </Panel>
              <Panel title="THALIA feed" meta={wsConnected ? <span style={{ color: 'var(--g)' }}>● live</span> : <span style={{ color: 'var(--y)' }}>● sim</span>} span="s5">
                <ThaliaFeed lines={feed} />
              </Panel>

              <Panel title="THALIA hint" meta="read the clock" span="s7">
                <SafeSection name="THALIAHint"><Suspense fallback={null}>
                  <THALIAHint timeLeft={displayTimeRemaining} keyPrice={displayCurrentPrice} isAssassinMode={assassinActive} isFlashCrash={flashCrashActive} isFOMOSurge={fomoSurgeActive} expanded={true} />
                </Suspense></SafeSection>
              </Panel>
              <Panel title="Key buys / min" meta="live activity" span="s5" className="hideM">
                <Waveform bumpKey={buyPulse} color={urgency.active ? urgency.color : '#7dff9b'} />
              </Panel>

              <Panel title="Live activity" span="s12" className="hideM">
                <SafeSection name="LiveActivityFeed"><Suspense fallback={null}><LiveActivityFeed /></Suspense></SafeSection>
              </Panel>
              <div className="s12 hideM">
                <SafeSection name="Dashboard"><Suspense fallback={null}>
                  <Dashboard pot={displayPot} currentPrice={displayCurrentPrice} lastBuyer={displayLastBuyer} account={account} refreshTrigger={refreshTrigger} />
                </Suspense></SafeSection>
              </div>
              <Panel title="Share" meta="post to X" span="s12">
                <SafeSection name="Sharing"><Suspense fallback={null}><Sharing account={account} pot={pot} timeLeft={timeRemaining} /></Suspense></SafeSection>
              </Panel>
            </div>
          )}

          {currentPage === 'war' && (
            <div className="holo-grid" data-tutorial="teams">
              <Panel title="War Room · faction standings" meta="share of keys" span="s7">
                <SafeSection name="FactionRings"><FactionLeaderboard variant="rings" playerFaction={playerTeam} refreshTrigger={refreshTrigger} /></SafeSection>
              </Panel>
              <Panel title="Faction leaderboard" span="s5">
                <SafeSection name="FactionLeaderboard"><FactionLeaderboard playerFaction={playerTeam} refreshTrigger={refreshTrigger} /></SafeSection>
              </Panel>
              {SEASONS_LIVE && (
                <Panel title="Season" meta="30 days, points only" span="s12">
                  <SafeSection name="SeasonsLive"><Suspense fallback={null}><SeasonsLive account={account} /></Suspense></SafeSection>
                </Panel>
              )}
              {SEASONS_LIVE && (
                <Panel title="Faction war room" meta="season race, points only" span="s6">
                  <SafeSection name="WarRoom"><Suspense fallback={null}><WarRoom faction={playerTeam} /></Suspense></SafeSection>
                </Panel>
              )}
              <Panel title="Your stakes" meta="this round, test FRAME" span="s6">
                <SafeSection name="StakesPanel"><Suspense fallback={null}><StakesPanel account={account} roundId={currentRoundId} faction={playerTeam} /></Suspense></SafeSection>
              </Panel>
              {CHAOS_PANEL_ENABLED && (
                <Panel title="Chaos events" meta="Charity + Carnival (test FRAME)" span="s12">
                  <SafeSection name="ChaosRoundPanel"><Suspense fallback={null}><ChaosRoundPanel /></Suspense></SafeSection>
                </Panel>
              )}
              {account && playerTeam > 0 && (
                <Panel title="Faction chat" span="s6" tilt={false}>
                  <TeamChat team={playerTeam} playerAddress={account} isOpen={true} onClose={() => {}} />
                </Panel>
              )}
              {account ? (
                <Panel title="Your progression" span="s6">
                  <SafeSection name="ResistancePoints"><Suspense fallback={null}><ResistancePoints account={account} /></Suspense></SafeSection>
                  <SafeSection name="DegenTitles"><Suspense fallback={null}><DegenTitles account={account} compact /></Suspense></SafeSection>
                  <SafeSection name="StreakDisplay"><Suspense fallback={null}><StreakDisplay address={account} compact /></Suspense></SafeSection>
                </Panel>
              ) : (
                <Panel title="Join a faction" span="s6"><p style={{ color: 'var(--t2)' }}>Connect a wallet and buy a key to choose your faction.</p></Panel>
              )}
            </div>
          )}

          {currentPage === 'leaderboard' && (
            <div className="holo-grid">
              <Panel title="Leaderboard" meta={`round #${currentRoundId}`} span="s7">
                <SafeSection name="Leaderboard"><Suspense fallback={null}><Leaderboard refreshTrigger={refreshTrigger} /></Suspense></SafeSection>
              </Panel>
              <Panel title="Referrals" span="s5">
                <SafeSection name="Referral"><Suspense fallback={null}><Referral account={account} /></Suspense></SafeSection>
              </Panel>
              <Panel title="Share" meta="post to X" span="s12">
                <SafeSection name="Sharing"><Suspense fallback={null}><Sharing account={account} pot={pot} timeLeft={timeRemaining} /></Suspense></SafeSection>
              </Panel>
            </div>
          )}

          {currentPage === 'history' && (
            <div className="holo-grid">
              <Panel title="Round replay" meta="every buy · shareable link" span="s12">
                <SafeSection name="RoundReplay"><Suspense fallback={null}><RoundReplay roundId={profileAddr && /^\d+$/.test(profileAddr) ? Number(profileAddr) : 0} onPick={(id) => setCurrentPage('history', String(id))} /></Suspense></SafeSection>
              </Panel>
              <Panel title="Round history" meta="test FRAME · testnet" span="s12">
                <SafeSection name="RoundHistory"><Suspense fallback={null}><RoundHistory refreshTrigger={refreshTrigger} /></Suspense></SafeSection>
              </Panel>
              {currentRoundId > 1 && (
                <Panel title="Pioneer & faction payouts" meta="live on-chain · Phase 1" span="s5">
                  <div className="mono" style={{ fontSize: 13, color: '#9fc3c9', lineHeight: 1.5 }}>
                    <b>Live since round 1210.</b> At round end the 5% pioneer pool goes to one early buyer, drawn by keys bought in the first 25% of key sales. The 3% faction pot is split 40/25/15% among the top three factions, plus 20% to the winner&apos;s faction. Members collect their part with <b>Claim all</b> in the <button type="button" className="yk-link" onClick={() => setCurrentPage('play')}>Your keys</button> panel on Play, for every finished round. Faction powers are Phase 2 and not live yet.
                  </div>
                </Panel>
              )}
              <Panel title="Game log" span={currentRoundId > 1 ? 's7' : 's12'}>
                <SafeSection name="GameLog"><Suspense fallback={null}><GameLog /></Suspense></SafeSection>
              </Panel>
              <Panel title="Analytics" span="s12">
                <SafeSection name="Analytics"><Suspense fallback={null}><AnalyticsDashboard account={account} /></Suspense></SafeSection>
              </Panel>
              <Panel title="Your transactions" span="s12">
                <SafeSection name="TransactionHistory"><Suspense fallback={null}><TransactionHistory account={account} /></Suspense></SafeSection>
              </Panel>
            </div>
          )}

          {currentPage === 'howto' && (
            <div className="holo-grid">
              <Panel title="How it works" span="s12" tilt={false}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                  <button type="button" className="holo-btn pri" onClick={() => { setCurrentPage('play'); howItWorks.reopen(); }}>Show the 1-minute tour</button>
                  <button type="button" className="holo-btn ghost" onClick={() => setCurrentPage('terms')}>Terms &amp; privacy</button>
                </div>
                <HowToPlay />
              </Panel>
              <Panel title="THALIA Drones: rules & FAQ" span="s12" tilt={false}>
                <DronesFaq />
              </Panel>
              <Panel title="Lore" meta="coming" span="s12" tilt={false}>
                <LoreDrops />
              </Panel>
            </div>
          )}

          {currentPage === 'thesis' && (
            <SafeSection name="Thesis"><Suspense fallback={loader}>
              <ThesisPage onPlay={() => setCurrentPage('play')} onClose={() => setCurrentPage('play')} />
            </Suspense></SafeSection>
          )}

          {currentPage === 'admin' && (
            <SafeSection name="AdminPage"><Suspense fallback={loader}>
              <AdminPage account={account} />
            </Suspense></SafeSection>
          )}

          {currentPage === 'profile' && (
            <SafeSection name="ProfilePage">
              <ProfilePage address={profileAddr} account={account} roundId={currentRoundId} lastBuyer={displayLastBuyer} onPlay={() => setCurrentPage('play')} onViewSelf={() => setCurrentPage('profile')} />
            </SafeSection>
          )}

          {currentPage === 'terms' && (
            <div className="holo-grid">
              <div className="s12">
                <SafeSection name="TermsOfService"><Suspense fallback={loader}>
                  <TermsOfService onBack={() => setCurrentPage('play')} />
                </Suspense></SafeSection>
              </div>
            </div>
          )}
          </Suspense>

          {/* ═══ FOOTER ═══ */}
          <footer className="holo-footer">
            <div style={{ marginBottom: 10 }}><SafeSection name="NetworkStatus"><Suspense fallback={null}><NetworkStatusBar /></Suspense></SafeSection></div>
            <div>NO TOMORROW 3D · Humanity's last stand against THALIA · <span className="holo-demo">● TESTNET DEMO</span></div>
            <div style={{ marginTop: 8, display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
              {!demoMode.isDemo && <button type="button" onClick={() => setShowDemoModal(true)}>Try demo mode (practice with test FRAME)</button>}
              <button type="button" onClick={() => setCurrentPage('thesis')}>Thesis</button>
              <button type="button" onClick={() => setCurrentPage('terms')}>Terms of Service</button>
              <button type="button" onClick={() => setCurrentPage('terms')}>Privacy Policy</button>
            </div>
          </footer>
        </div>
      </div>

      {/* ═══ MOBILE BOTTOM TAB BAR ═══ */}
      <nav className="holo-tabbar" aria-label="Console tabs">
        {RAIL_TABS.map(t => (
          <button key={t.id} type="button" className={currentPage === t.id ? 'on' : ''} onClick={() => setCurrentPage(t.id)}>
            <i>{t.icon}</i>{t.short}
          </button>
        ))}
      </nav>

      <InstallPrompt variant="banner" />

      {account && playerTeam > 0 && currentPage !== 'war' && (
        <button
          onClick={() => setShowTeamChat(!showTeamChat)}
          className="lg:hidden fixed bottom-20 right-4 z-40 holo-btn ghost sm"
          title="Faction Chat"
        >
          💬 Faction Chat
        </button>
      )}
      {showTeamChat && account && (
        <div className="fixed bottom-32 right-4 z-50 w-80">
          <TeamChat team={playerTeam} playerAddress={account} isOpen={showTeamChat} onClose={() => setShowTeamChat(false)} />
        </div>
      )}

      <SafeSection name="NarrativeToast">
      {narrativeEvent && (
        <NarrativeToast event={narrativeEvent} roundId={currentRoundId} onDismiss={() => setNarrativeEvent(undefined)} autoHide={10000} />
      )}
      </SafeSection>
    </div>
    </NotificationProvider>
  );
}
